import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import {
  FormLayout,
  TextField,
  Form,
  Button,
  Banner,
} from "@shopify/polaris";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { createOrUpdateBusinessTripReport } from "services/business-trip.service";
import { AppDispatch } from "configureStore";
import { setIsModalxOpen } from "components/modalx/modalx.slice";
import { fetchBusinessTripDetails } from "containers/business-trip-view-container/business-trip-container.slice";
import { BusinessTrip } from "types/BusinessTrip";

interface BusinessTripReportFormProps {
  tripId: string;
  /** Prefill: report fields when editing; trip dates/purpose are never shown, backend uses trip. */
  trip?: Partial<BusinessTrip>;
}

const BusinessTripReportForm: React.FC<BusinessTripReportFormProps> = ({
  tripId,
  trip,
}) => {
  const dispatch: AppDispatch = useDispatch();
  const { t } = useTranslation();
  const { companyId } = useParams<{ companyId: string }>();

  const [tripResults, setTripResults] = useState("");
  const [comments, setComments] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState<string | undefined>();
  const [errMsg, setErrMsg] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(false);

  const isEditMode = !!(
    trip &&
    ((trip.tripResults != null && String(trip.tripResults).trim() !== "") ||
      (trip.comments != null && String(trip.comments).trim() !== ""))
  );

  useEffect(() => {
    if (trip) {
      setTripResults(trip.tripResults ?? "");
      setComments(trip.comments ?? "");
    }
  }, [trip]);

  const handleFormSubmit = async () => {
    if (!companyId) return;
    setErrors({});

    const newErrors: Record<string, string> = {};
    if (!tripResults || tripResults.trim() === "") {
      newErrors.tripResults = t("field_is_required") || "Field is required";
    }
    if (!comments || comments.trim() === "") {
      newErrors.comments = t("field_is_required") || "Field is required";
    }
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    try {
      setIsLoading(true);
      await createOrUpdateBusinessTripReport(companyId, tripId, {
        tripResults: tripResults || undefined,
        comments: comments || undefined,
      });
      setSuccessMessage("ok");
      dispatch(fetchBusinessTripDetails({ companyId, tripId }));
      setTimeout(() => {
        dispatch(setIsModalxOpen(false));
      }, 1500);
    } catch (e) {
      setErrMsg("error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Form onSubmit={handleFormSubmit}>
      {(errMsg || successMessage) && (
        <div style={{ marginBottom: "16px" }}>
          {errMsg && <Banner tone="critical">{t(errMsg)}</Banner>}
          {successMessage && (
            <Banner tone="success">{t(successMessage)}</Banner>
          )}
        </div>
      )}
      <FormLayout>
        <TextField
          label={t("trip_results")}
          value={tripResults}
          onChange={setTripResults}
          multiline={3}
          autoComplete="off"
          requiredIndicator={true}
          error={errors.tripResults}
        />
        <TextField
          label={t("comments")}
          value={comments}
          onChange={setComments}
          multiline={3}
          autoComplete="off"
          requiredIndicator={true}
          error={errors.comments}
        />
        <Button submit loading={isLoading}>
          {t(isEditMode ? "update" : "save")}
        </Button>
      </FormLayout>
    </Form>
  );
};

export default BusinessTripReportForm;
